import { useEffect } from "react";

/**
 * Custom hook to create and expose DOI to Zotero for direct saving
 * @param doi The DOI to expose to Zotero
 */
export const useZoteroMetadata = (doi?: string) => {
  useEffect(() => {
    if (!doi) return;

    // Create a container for Zotero metadata
    const container = document.createElement("div");
    container.style.display = "none";

    // Add DOI in the format that Zotero's content scripts look for
    const doiSpan = document.createElement("span");
    doiSpan.className = "zotero-doi";
    doiSpan.setAttribute("data-doi", doi);
    doiSpan.textContent = `doi:${doi}`; // Add visible text content with doi: prefix
    container.appendChild(doiSpan);

    // Add a second span with just the DOI as text content
    const plainSpan = document.createElement("span");
    plainSpan.className = "doi";
    plainSpan.textContent = doi;
    container.appendChild(plainSpan);

    // Add the container to the document
    document.body.appendChild(container);

    // Force Zotero to refresh metadata
    const refreshMetadata = () => {
      // Create and dispatch a custom event that includes the DOI
      const event = new CustomEvent("ZoteroItemUpdated", {
        bubbles: true,
        cancelable: true,
        detail: { doi },
      });
      document.dispatchEvent(event);
    };

    // Immediate refresh
    refreshMetadata();

    // Additional refreshes with increasing delays (necessary for Zotero to detect the metadata)
    const timeouts = [
      setTimeout(refreshMetadata, 50),
      setTimeout(refreshMetadata, 200),
      setTimeout(refreshMetadata, 500),
      setTimeout(refreshMetadata, 1000),
      setTimeout(refreshMetadata, 2000),
    ];

    // Cleanup
    return () => {
      document.body.removeChild(container);
      timeouts.forEach((timeout) => clearTimeout(timeout));
    };
  }, [doi]);
};
